/**
 * LLM text parsing utilities.
 *
 * Handles the shared quirks of LLM output: reasoning-tag wrappers, markdown
 * code fences, preamble text around JSON payloads. Used by both the
 * `ai-chatbot` module (backend-tms) and the `@axon/agent-core` planner
 * executor — keeping a single copy here prevents drift in how we normalize
 * provider output.
 *
 * @module common/utils/llm-text
 */

/**
 * Matches `<think>…</think>` blocks emitted by reasoning models
 * (GLM-4, DeepSeek-R1, QwQ, etc.). The inner chain-of-thought must be
 * stripped before either user display or JSON parse.
 */
const THINKING_TAG_PATTERN = /<think>[\s\S]*?<\/think>/g;

/**
 * Matches fenced JSON (```json … ``` or ``` … ```) and captures the body.
 */
const MARKDOWN_FENCE_PATTERN = /```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/;

/**
 * Extract the FIRST balanced JSON object/array region from free text.
 *
 * Replaces the old greedy `\{[\s\S]*\}` regex extraction, which spanned
 * from the first `{` to the LAST `}` in the text — any epilogue prose
 * containing a brace (MiniMax-M3 likes to append commentary after its
 * JSON step) made the extracted region unparseable. A depth-tracking
 * scan with string/escape awareness stops at the matching close
 * bracket, so arbitrary prose before AND after the payload is fine.
 *
 * Returns `null` when no complete balanced region exists.
 */
export function extractFirstJsonRegion(text: string): string | null {
  const start = text.search(/[{[]/);
  if (start === -1) return null;
  const open = text[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      if (inString) escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === open) depth++;
    else if (ch === close) {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

export type TypeGuard<T> = (parsed: unknown) => parsed is T;

/**
 * Remove reasoning-tag blocks and trim. Idempotent.
 */
export function stripThinkingTags(content: string): string {
  return content.replace(THINKING_TAG_PATTERN, '').trim();
}

/**
 * Streaming-safe `<think>` tag filter for LLM stream chunks.
 *
 * Reasoning models (MiniMax-M2, GLM-4, DeepSeek-R1, QwQ via llama.cpp, …)
 * may open a `<think>` block at the start of the stream whose closing tag
 * arrives many chunks later. `stripThinkingTags` only works on complete
 * strings, so streaming consumers use this stateful filter instead: it
 * buffers through the detection window and the thinking block, and only
 * emits content that is outside thinking tags.
 *
 * Moved here from `backend-tms/ai-chatbot/utils/text.utils.ts` so the
 * think-capable LLM adapters (`llm-core`) and any other stream consumer
 * can filter at the source (issue #1129).
 *
 * @example
 * ```typescript
 * const filter = new ThinkingTagStreamFilter();
 * for await (const chunk of llmStream) {
 *   const filtered = filter.processChunk(chunk.content);
 *   if (filtered) {
 *     yield { content: filtered, isComplete: false };
 *   }
 * }
 * const remaining = filter.flush();
 * ```
 */
export class ThinkingTagStreamFilter {
  /** Internal buffer for accumulating content */
  private buffer = '';

  /** Whether we are currently inside a <think> block */
  private insideThink = false;

  /** Whether the initial detection phase is complete */
  private detectionComplete = false;

  /** Maximum bytes to buffer before deciding there are no think tags */
  private static readonly DETECTION_THRESHOLD = 20;

  /**
   * Process a stream chunk and return filtered content.
   *
   * @param content - Raw chunk content from LLM stream
   * @returns Filtered content to emit, or empty string if buffering
   */
  processChunk(content: string): string {
    if (!content) return '';

    this.buffer += content;

    // Phase 1: Detection - buffer initial content to check for <think> tag
    if (!this.detectionComplete) {
      const trimmed = this.buffer.trimStart();

      if (trimmed.startsWith('<think>')) {
        this.insideThink = true;
        this.detectionComplete = true;
        // Fall through to Phase 2 to check for closing tag
      } else if (trimmed.length >= ThinkingTagStreamFilter.DETECTION_THRESHOLD) {
        // No think tag found in initial content - pass through
        this.detectionComplete = true;
        const output = this.buffer;
        this.buffer = '';
        return output;
      } else {
        // Need more data for detection
        return '';
      }
    }

    // Phase 2: Inside thinking block - look for closing tag
    if (this.insideThink) {
      const closeIdx = this.buffer.indexOf('</think>');
      if (closeIdx !== -1) {
        // Found closing tag - extract content after it
        const afterThink = this.buffer.substring(closeIdx + '</think>'.length);
        this.buffer = '';
        this.insideThink = false;
        return afterThink;
      }
      // Still inside think block, keep buffering
      return '';
    }

    // Phase 3: Normal pass-through (after detection, not inside think)
    const output = this.buffer;
    this.buffer = '';
    return output;
  }

  /**
   * Flush any remaining buffered content.
   * Call this when the stream is complete.
   *
   * @returns Any remaining content that hasn't been emitted
   */
  flush(): string {
    if (this.insideThink) {
      // Stream ended while inside think block - discard thinking content
      this.buffer = '';
      return '';
    }
    const remaining = this.buffer;
    this.buffer = '';
    return remaining;
  }
}

/**
 * If the content is wrapped in a markdown code fence, return the fence body.
 * Otherwise trim and return as-is.
 */
export function stripMarkdownFences(raw: string): string {
  const fenced = raw.match(MARKDOWN_FENCE_PATTERN);
  return fenced ? fenced[1].trim() : raw.trim();
}

/**
 * Escape raw control characters that appear INSIDE a JSON string literal.
 *
 * Chat-tuned models routinely emit a JSON object whose string value is a
 * multi-line markdown payload (a `final` answer with tables/headings) using
 * LITERAL newlines/tabs — which is invalid JSON (`JSON.parse` rejects raw
 * control chars in strings). This walks the text with string/escape awareness
 * and escapes control chars that occur inside a string, leaving the JSON
 * structure untouched so the object can be recovered. No-op for well-formed
 * JSON (it has no raw control chars inside strings).
 */
export function escapeControlCharsInJsonStrings(text: string): string {
  let out = '';
  let inString = false;
  let escaped = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (escaped) {
      out += ch;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      out += ch;
      escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      out += ch;
      continue;
    }
    if (inString) {
      const code = text.charCodeAt(i);
      if (ch === '\n') {
        out += '\\n';
        continue;
      }
      if (ch === '\r') {
        out += '\\r';
        continue;
      }
      if (ch === '\t') {
        out += '\\t';
        continue;
      }
      if (code < 0x20) {
        out += '\\u' + code.toString(16).padStart(4, '0');
        continue;
      }
    }
    out += ch;
  }
  return out;
}

/**
 * Parse JSON from LLM output with layered recovery:
 *   1. strip thinking tags + markdown fences
 *   2. direct `JSON.parse`
 *   3. extract the first bracketed/braced region and parse that
 *   4. repair raw control chars inside string literals and retry (handles a
 *      JSON object whose value is multi-line markdown with literal newlines)
 *
 * Throws when a region parses but fails the validator (type-guard), or when no
 * parseable region is found — the two cases carry distinct messages.
 */
export function parseLLMJson<T>(raw: string, validator: TypeGuard<T>): T {
  const cleaned = stripMarkdownFences(stripThinkingTags(raw));
  const region = extractFirstJsonRegion(cleaned);

  // Ordered parse candidates; later ones are progressively repaired.
  const candidates = [cleaned, region, escapeControlCharsInJsonStrings(region ?? cleaned)];

  let parsedButInvalid = false;
  for (const candidate of candidates) {
    if (!candidate) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(candidate);
    } catch {
      continue;
    }
    if (validator(parsed)) return parsed;
    parsedButInvalid = true;
  }

  if (parsedButInvalid) {
    throw new Error('Extracted JSON failed type guard validation');
  }
  throw new Error(`Failed to parse LLM JSON output: ${cleaned.substring(0, 200)}`);
}
